import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Modal, Button } from "react-bootstrap";
import { FIELD_TYPES } from "./emailTemplateFieldsConfig";
import {
  buildPreviewBlocks,
  getFieldPreviewHtml,
  isGreetingField,
  isLabelField,
  isMainTitleField,
  getPreviewContentFields,
} from "./emailPreviewUtils";

function PreviewSingleBlock({ field, watch }) {
  const html = getFieldPreviewHtml(field, watch);
  if (!html) return null;

  if (isMainTitleField(field) || field.name === "basic_mail_header") {
    return <h4 className="text-center mb-3">{html}</h4>;
  }

  if (isGreetingField(field)) {
    return <p className="mb-2">{html}</p>;
  }

  if (isLabelField(field)) {
    return <p className="small text-muted mb-1">{html}</p>;
  }

  if (field.type === FIELD_TYPES.RICHTEXT || html.includes("<")) {
    return (
      <div
        className="mb-3"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  }

  return <p className="mb-2">{html}</p>;
}

function PreviewHighlightBlock({ titleField, bodyField, watch }) {
  const titleHtml = getFieldPreviewHtml(titleField, watch);
  const bodyHtml = getFieldPreviewHtml(bodyField, watch);
  if (!titleHtml && !bodyHtml) return null;

  const isWarning = titleField.name.includes("failed") || titleField.name.endsWith("_heading");

  return (
    <div className={`alert ${isWarning ? "alert-warning" : "alert-success"} mb-3`}>
      {titleHtml && <h6 className="alert-heading mb-2">{titleHtml}</h6>}
      {bodyHtml &&
        (bodyHtml.includes("<") ? (
          <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : (
          <p className="mb-0">{bodyHtml}</p>
        ))}
    </div>
  );
}

export function EmailPreviewContent({ fields, watch }) {
  const { t } = useTranslation();
  const blocks = buildPreviewBlocks(fields);
  const hasContent = blocks.some((block) => {
    if (block.type === "highlight") {
      return (
        getFieldPreviewHtml(block.titleField, watch) ||
        getFieldPreviewHtml(block.bodyField, watch)
      );
    }
    return getFieldPreviewHtml(block.field, watch);
  });

  if (!hasContent) {
    return <p className="text-muted mb-0">{t("No content to preview yet.")}</p>;
  }

  return (
    <div className="border rounded p-3 bg-white">
      {blocks.map((block) => {
        if (block.type === "highlight") {
          return (
            <PreviewHighlightBlock
              key={`${block.titleField.name}-${block.bodyField.name}`}
              titleField={block.titleField}
              bodyField={block.bodyField}
              watch={watch}
            />
          );
        }

        return (
          <PreviewSingleBlock
            key={block.field.name}
            field={block.field}
            watch={watch}
          />
        );
      })}
    </div>
  );
}

export default function EmailTemplatePreview({ sectionLabel, fields, watch }) {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const contentFields = getPreviewContentFields(fields);

  if (!contentFields.length) return null;

  return (
    <>
      <Button variant="outline-primary" size="sm" onClick={() => setShow(true)}>
        <i className="bi bi-eye me-1" />
        {t("Live Preview")}
      </Button>

      <Modal show={show} onHide={() => setShow(false)} size="lg" scrollable centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-envelope-open me-2" />
            {t("Live Preview")}
            <span className="text-muted ms-2">— {t(sectionLabel)}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="text-muted small mb-3">{t("Sample data")}</p>
          <EmailPreviewContent fields={fields} watch={watch} />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShow(false)}>
            {t("Close")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
